import React, { useRef } from 'react';
import { motion, useAnimationFrame, useMotionTemplate, useMotionValue, useTransform } from 'framer-motion';

interface MovingBorderProps {
  borderRadius?: string;
  children: React.ReactNode;
  containerClassName?: string;
  borderClassName?: string;
  innerClassName?: string;
  duration?: number;
  className?: string;
  as?: any;
  [key: string]: any;
}

/**
 * Moving Border（Aceternity UI 风格）— 沿边框移动的光点
 */
export function MovingBorder({
  borderRadius = '0.75rem',
  children,
  containerClassName,
  borderClassName,
  innerClassName,
  duration = 3000,
  className,
  as: Component = 'button',
  ...otherProps
}: MovingBorderProps) {
  return (
    <Component
      className={'relative overflow-hidden bg-transparent p-[1px] ' + (containerClassName || '')}
      style={{ borderRadius }}
      {...otherProps}
    >
      <div className="absolute inset-0" style={{ borderRadius: `calc(${borderRadius} * 0.96)` }}>
        <MovingBorderCore duration={duration} rx="30%" ry="30%">
          <div
            className={'h-24 w-24 opacity-90 ' + (borderClassName || '')}
            style={{ background: 'radial-gradient(#e11d2e 30%, #ff5c75 45%, transparent 70%)' }}
          />
        </MovingBorderCore>
      </div>
      <div
        className={
          'relative flex items-center justify-center ' +
          (innerClassName || 'border border-border-soft bg-card/90 backdrop-blur-xl ') +
          (className || '')
        }
        style={{ borderRadius: `calc(${borderRadius} * 0.96)` }}
      >
        {children}
      </div>
    </Component>
  );
}

const MovingBorderCore = ({
  children,
  duration,
  rx,
  ry,
}: {
  children: React.ReactNode;
  duration: number;
  rx: string;
  ry: string;
}) => {
  const pathRef = useRef<SVGRectElement>(null);
  const progress = useMotionValue(0);

  useAnimationFrame((time) => {
    const length = pathRef.current?.getTotalLength();
    if (length) {
      const pxPerMs = length / duration;
      progress.set((time * pxPerMs) % length);
    }
  });

  const x = useTransform(progress, (val) => pathRef.current?.getPointAtLength(val).x ?? 0);
  const y = useTransform(progress, (val) => pathRef.current?.getPointAtLength(val).y ?? 0);
  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;

  return (
    <>
      <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="absolute h-full w-full" width="100%" height="100%">
        <rect fill="none" width="100%" height="100%" rx={rx} ry={ry} ref={pathRef} />
      </svg>
      <motion.div style={{ position: 'absolute', top: 0, left: 0, display: 'inline-block', transform }}>
        {children}
      </motion.div>
    </>
  );
};
